import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LuCamera, LuChartPie, LuLightbulb } from 'react-icons/lu';
import { useAuth } from '../firebase/AuthContext';
import '../styles/components/ainutritionlandingpage.css';

const dashboardImageStyle = {
  width: '100%',
  borderRadius: '12px',
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
  border: '1px solid rgba(148, 163, 184, 0.2)',
  backgroundColor: '#ffffff'
};

const dashboardImageWrapperStyle = {
  marginBottom: '1rem'
};

const AiNutritionLandingPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const handleGetStarted = event => {
    event.preventDefault();

    if (currentUser) {
      navigate('/my-nutrition-status');
      return;
    }

    navigate('/signin');
  };

  return (
    <main className="phn-landing-wrapper">
      <section className="hero-section">
        <h1 className="mb-1">Unlock Smarter Eating with AI</h1>
        <p className="phn-trial-badge">
          <span className="phn-trial-badge-icon" aria-hidden="true"></span>
          <span className="phn-trial-badge-text">
            <strong>Free 15 day trial, no credit card required.</strong>
          </span>
        </p>

        <p>
          Pure Health Nexus is your AI-powered nutrition and weight-loss app
          that shows you exactly what you are really eating. Snap a photo,
          speak, or log your meals to get clear nutrition labels, intuitive
          dashboards, nutrient insights, and a realistic, personalized
          weight-loss plan.
        </p>

        <button
          type="button"
          className="btn-signin"
          onClick={handleGetStarted}
        >
          Get Started
        </button>
      </section>

      <section className="section bg-light section-nutrition-label">
        <div className="container">
          <h1 className="text-center mb-3">
            My personalized Nutrition Fact Label
          </h1>

          <div className="preview-image">
            <img
              src="/assets/images/Nutrition.jpg"
              alt="My personalized Nutrition Fact Label"
              className="nutrition-fact-label-image"
            />
          </div>
        </div>
      </section>

      <section className="section section-nutrition-impact">
        <div className="container">
          <div className="split-layout">
            <h1>How is my nutrition intake affecting my body?</h1>

            <div className="preview-image">
              <div className="ui-mockup">
                <div className="ui-header">
                  <div className="ui-title">My Nutrition</div>
                  <div className="ui-badge">Upgrade</div>
                </div>

                <div className="ui-tabs">
                  <div className="ui-tab active">Vitamins</div>
                  <div className="ui-tab">Minerals</div>
                  <div className="ui-tab">Fats</div>
                </div>

                <div className="ui-chart-area">
                  <div className="chart-outer">
                    <div className="chart-inner">
                      <div className="chart-text">Vitamins</div>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    textAlign: 'center',
                    fontSize: '0.85rem',
                    color: '#94a3b8',
                    marginTop: '-10px'
                  }}
                >
                  Interactive two-ring visualization
                </div>
              </div>
            </div>

            <div className="content-side">
              <h2>Why Pure Health Nexus?</h2>
              <p>
                Weight gain usually happens gradually when we eat more energy
                than we use. Over time, higher body weight and body fat are
                linked with an increased risk of type 2 diabetes, heart and
                blood vessel disease, joint problems and sleep issues, but most
                people only ever see calories, not the full pattern behind
                those changes.
              </p>
              <p>
                At the same time, many modern diets are energy-dense but lower
                in key vitamins, minerals and fibre, especially when they rely
                on refined grains and highly processed foods. Research links
                long-term low or high intakes of nutrients such as iodine,
                iron, vitamin D and vitamin B12 with symptoms like fatigue,
                feeling cold, muscle weakness, mood changes and gradual shifts
                in wellbeing.
              </p>
              <p>
                Pure Health Nexus combines AI meal logging with science-based
                energy and nutrient targets so you can see how your real-world
                eating pattern lines up with your needs and where small,
                realistic adjustments could help over time.
              </p>

              <div
                style={{
                  margin: '30px 0 20px',
                  padding: '20px',
                  background: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
                  borderLeft: '4px solid #00C6FF'
                }}
              >
                <p style={{ marginBottom: '5px', color: 'var(--text-main)' }}>
                  <strong>Micronutrients matter more than you think</strong>
                </p>
                <p style={{ marginTop: 0, fontSize: '0.95rem' }}>
                  <strong>Thyroid &amp; weight:</strong> Iodine status is one
                  factor in thyroid function, which helps regulate energy,
                  temperature and body weight.
                </p>
              </div>

              <ul>
                <li>
                  <strong>Clarity:</strong> See how your intake compares to
                  recommended macros, vitamins and minerals.
                </li>
                <li>
                  <strong>Actionable guidance:</strong> Get concrete,
                  food-level suggestions to improve meals over time.
                </li>
                <li>
                  <strong>Privacy-focused:</strong> Your nutrition data is
                  yours. No selling data.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-light">
        <div className="container">
          <h2 className="text-center mb-3">How My Nutrition Works</h2>
          <div className="grid-3">
            <div className="feature-card">
              <span className="icon-box" aria-hidden="true">
                <LuCamera />
              </span>
              <h3>Log what you actually eat</h3>
              <p>
                Use photo, microphone or quick text to capture meals. The AI
                breaks them into foods, portions, and nutrients.
              </p>
            </div>
            <div className="feature-card">
              <span className="icon-box" aria-hidden="true">
                <LuChartPie />
              </span>
              <h3>See nutrient distribution</h3>
              <p>
                Donut charts views show how vitamins, minerals, and macros
                stack up against your targets.
              </p>
            </div>
            <div className="feature-card">
              <span className="icon-box" aria-hidden="true">
                <LuLightbulb />
              </span>
              <h3>Get smart suggestions</h3>
              <p>
                See simple food swaps and add-ons that help close gaps without
                rebuilding your entire diet.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2 className="text-center mb-3">My Nutrition Status Dashboard</h2>
          <div className="grid-3">
            <div className="feature-card">
              <div style={dashboardImageWrapperStyle}>
                <img
                  src="/assets/images/My_Nutrition_Status_Vitamin.jpg"
                  alt="My Nutrition Status vitamins dashboard"
                  style={dashboardImageStyle}
                />
              </div>
              <h2>Vitamins</h2>
              <h3>Track your vitamin trends</h3>
              <p>
                See which vitamins are low, review food ideas that help close
                gaps, and follow your vitamin status over time so patterns are
                easier to spot.
              </p>
            </div>

            <div className="feature-card">
              <div style={dashboardImageWrapperStyle}>
                <img
                  src="/assets/images/My_Nutrition_Status_Mineral.jpg"
                  alt="My Nutrition Status minerals dashboard"
                  style={dashboardImageStyle}
                />
              </div>
              <h2>Minerals</h2>
              <h3>Monitor key mineral gaps</h3>
              <p>
                Review important minerals like iron, magnesium, zinc, and
                calcium so you can see where your intake may be falling short
                and what foods may help improve it.
              </p>
            </div>

            <div className="feature-card">
              <div style={dashboardImageWrapperStyle}>
                <img
                  src="/assets/images/My_Nutrition_Status_Fats.jpg"
                  alt="My Nutrition Status fats dashboard"
                  style={dashboardImageStyle}
                />
              </div>
              <h2>Fats</h2>
              <h3>Understand fat quality and balance</h3>
              <p>
                Compare your fat intake with recommended ranges so you can see
                how saturated fats, omega-3s, and overall fat quality are
                affecting your long-term nutrition picture.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2 className="text-center mb-1">
            When nutrient levels are low or high
          </h2>
          <div className="nutrient-table-wrapper">
            <table className="nutrient-table">
              <thead>
                <tr>
                  <th>Nutrient</th>
                  <th>Low</th>
                  <th>High</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Folate (B9)</td>
                  <td>
                    Low folate is linked with megaloblastic anaemia and, in
                    pregnancy, a higher risk of neural tube defects in the baby.
                  </td>
                  <td>
                    High intakes of folic acid from supplements can hide
                    vitamin B12 deficiency by correcting anaemia but not nerve
                    damage.
                  </td>
                </tr>
                <tr>
                  <td>Folate + B12 (combo)</td>
                  <td>
                    Low folate and low B12 together increase the chance of
                    megaloblastic anaemia and can cause tiredness and
                    neurological symptoms.
                  </td>
                  <td>
                    Very high folic acid with low B12 can mask B12 deficiency;
                    high-dose B12 is usually well tolerated but should be
                    supervised if long term.
                  </td>
                </tr>
                <tr>
                  <td>Niacin (B3)</td>
                  <td>
                    Severe lack of niacin can cause pellagra, with diarrhoea,
                    inflamed skin and confusion.
                  </td>
                  <td>
                    High-dose supplements may cause skin flushing, itching and,
                    at very high levels, liver problems.
                  </td>
                </tr>
                <tr>
                  <td>Riboflavin (B2)</td>
                  <td>
                    Low riboflavin can lead to cracked lips, a sore swollen
                    tongue and skin or eye changes.
                  </td>
                  <td>
                    Extra riboflavin from food is usually excreted in urine;
                    toxicity from usual intakes is not commonly reported.
                  </td>
                </tr>
                <tr>
                  <td>Thiamin (B1)</td>
                  <td>
                    Severely low thiamin can cause beriberi, affecting nerves,
                    muscles and the heart, and can lead to weakness and heart
                    failure.
                  </td>
                  <td>
                    No clear toxicity from food has been identified; very high
                    supplement doses are not generally recommended without
                    supervision.
                  </td>
                </tr>
                <tr>
                  <td>Vitamin A</td>
                  <td>
                    Too little vitamin A can lead to night blindness and, in
                    severe cases, damage to the surface of the eye.
                  </td>
                  <td>
                    Excess preformed vitamin A from supplements or frequent
                    large portions of liver can be toxic and, in pregnancy, may
                    harm the developing baby.
                  </td>
                </tr>
                <tr>
                  <td>Vitamin B12</td>
                  <td>
                    Low vitamin B12 can cause megaloblastic anaemia, numbness,
                    tingling, balance problems and other neurological symptoms.
                  </td>
                  <td>
                    No upper limit from food has been established; very high
                    supplement doses are usually reserved for medical treatment.
                  </td>
                </tr>
                <tr>
                  <td>Vitamin B6</td>
                  <td>
                    Low B6 can contribute to anaemia, skin rashes and
                    nerve-related symptoms such as irritability or numbness.
                  </td>
                  <td>
                    Very high supplemental doses over time can cause nerve
                    damage with tingling, pain or loss of sensation in the
                    hands and feet.
                  </td>
                </tr>
                <tr>
                  <td>Vitamin C</td>
                  <td>
                    Severe vitamin C deficiency causes scurvy, with fatigue,
                    gum problems and easy bruising.
                  </td>
                  <td>
                    High-dose supplements may cause diarrhoea or stomach upset
                    and can contribute to kidney stones in susceptible people.
                  </td>
                </tr>
                <tr>
                  <td>Vitamin D</td>
                  <td>
                    Low vitamin D is linked with rickets in children and
                    osteomalacia in adults, conditions that weaken bones.
                  </td>
                  <td>
                    Very high supplement intakes over time can lead to vitamin D
                    toxicity with high blood calcium, kidney problems and
                    calcification of soft tissues.
                  </td>
                </tr>
                <tr>
                  <td>Vitamin E</td>
                  <td>
                    Low vitamin E is rare but, when severe, can cause nerve and
                    muscle problems, especially in people with fat-absorption
                    disorders.
                  </td>
                  <td>
                    Large supplemental doses may increase bleeding risk,
                    especially in people taking blood-thinning medicines.
                  </td>
                </tr>
                <tr>
                  <td>Vitamin K</td>
                  <td>
                    Very low intake can impair normal blood clotting and
                    increase bruising or bleeding.
                  </td>
                  <td>
                    High vitamin K from food is usually safe but can interfere
                    with certain blood-thinning medications.
                  </td>
                </tr>
                <tr>
                  <td>Biotin (B7)</td>
                  <td>
                    Severe deficiency is uncommon but can cause hair loss, skin
                    rashes and, in some cases, nerve symptoms.
                  </td>
                  <td>
                    High-dose biotin supplements can interfere with some
                    laboratory blood tests, leading to misleading results.
                  </td>
                </tr>
                <tr>
                  <td>Choline</td>
                  <td>
                    Low choline intake may contribute to liver or muscle
                    problems in some people.
                  </td>
                  <td>
                    Excess choline from supplements can cause low blood
                    pressure, sweating, fishy body odour and, at high levels,
                    liver toxicity.
                  </td>
                </tr>
                <tr>
                  <td>Pantothenic Acid (B5)</td>
                  <td>
                    Deficiency is rare but may lead to fatigue, irritability and
                    numbness or tingling.
                  </td>
                  <td>
                    Very high intakes from supplements can cause mild digestive
                    upset such as diarrhoea.
                  </td>
                </tr>
                <tr>
                  <td>Calcium</td>
                  <td>
                    Low calcium over time is linked with reduced bone strength,
                    osteopenia and osteoporosis.
                  </td>
                  <td>
                    Very high intakes, usually from supplements, can increase
                    the risk of kidney stones and may cause high blood calcium
                    in susceptible people.
                  </td>
                </tr>
                <tr>
                  <td>Copper</td>
                  <td>
                    Low copper can cause anaemia, low white blood cell counts
                    and neurological problems.
                  </td>
                  <td>
                    Excess copper, often from contaminated water or high-dose
                    supplements, can cause nausea and, in severe cases, liver
                    damage.
                  </td>
                </tr>
                <tr>
                  <td>Fluoride</td>
                  <td>
                    Low fluoride exposure is associated with a higher risk of
                    tooth decay in populations where water fluoridation is
                    standard.
                  </td>
                  <td>
                    Excess fluoride over time can cause dental or skeletal
                    fluorosis, which changes tooth enamel and may affect bones.
                  </td>
                </tr>
                <tr>
                  <td>Iodine</td>
                  <td>
                    Low iodine intake can lead to an enlarged thyroid (goitre)
                    and underactive thyroid function.
                  </td>
                  <td>
                    High intakes, especially from supplements or medicines, can
                    also disturb thyroid function in some people.
                  </td>
                </tr>
                <tr>
                  <td>Iron</td>
                  <td>
                    Low iron can contribute to iron-deficiency anaemia, causing
                    tiredness, weakness, pale skin and shortness of breath.
                  </td>
                  <td>
                    High-dose supplements can cause digestive side effects and,
                    in extreme cases, iron overload that may damage organs.
                  </td>
                </tr>
                <tr>
                  <td>Magnesium</td>
                  <td>
                    Low magnesium can cause muscle cramps, fatigue and, in more
                    serious cases, abnormal heart rhythms.
                  </td>
                  <td>
                    Very high intakes from supplements or medicines,
                    particularly in people with kidney problems, can lead to low
                    blood pressure and heart rhythm changes.
                  </td>
                </tr>
                <tr>
                  <td>Manganese</td>
                  <td>
                    Deficiency from diet alone is rare; low levels may affect
                    bone and metabolic health.
                  </td>
                  <td>
                    Excess manganese exposure, mainly from contaminated water or
                    inhalation in workplaces, can affect the nervous system.
                  </td>
                </tr>
                <tr>
                  <td>Molybdenum</td>
                  <td>
                    Dietary deficiency is very uncommon; low levels may impair
                    certain enzyme systems.
                  </td>
                  <td>
                    Very high intakes have been associated in some reports with
                    gout-like symptoms, but data are limited.
                  </td>
                </tr>
                <tr>
                  <td>Phosphorus</td>
                  <td>
                    Low phosphorus can weaken bones and, in severe cases, cause
                    muscle weakness.
                  </td>
                  <td>
                    High intakes, especially in people with kidney disease, may
                    contribute to vascular and soft-tissue calcification.
                  </td>
                </tr>
                <tr>
                  <td>Potassium</td>
                  <td>
                    Low potassium (hypokalaemia) can cause muscle weakness,
                    constipation and abnormal heart rhythms.
                  </td>
                  <td>
                    Very high potassium (hyperkalaemia), particularly with
                    kidney or adrenal problems, can disturb heart rhythm and be
                    serious.
                  </td>
                </tr>
                <tr>
                  <td>Potassium &amp; Sodium (K:Na)</td>
                  <td>
                    A low K:Na ratio (more sodium and less potassium) is linked
                    with higher blood pressure and cardiovascular risk.
                  </td>
                  <td>
                    A higher K:Na ratio from more potassium-rich and less salty
                    foods is generally favourable, though large changes should
                    be supervised in kidney or heart disease.
                  </td>
                </tr>
                <tr>
                  <td>Selenium</td>
                  <td>
                    Low selenium in some regions has been linked with certain
                    heart muscle problems and a weaker antioxidant defence.
                  </td>
                  <td>
                    Too much selenium (selenosis) can cause brittle hair and
                    nails, digestive upset and a metallic taste.
                  </td>
                </tr>
                <tr>
                  <td>Sodium</td>
                  <td>
                    Low blood sodium is usually related to fluid and medical
                    issues, and can cause headache, confusion and, in severe
                    cases, seizures.
                  </td>
                  <td>
                    High sodium intake is associated with elevated blood
                    pressure and higher risk of heart and kidney disease in many
                    people.
                  </td>
                </tr>
                <tr>
                  <td>Zinc</td>
                  <td>
                    Low zinc may weaken immune function, slow wound healing and
                    affect taste and growth.
                  </td>
                  <td>
                    Very high zinc intakes can cause nausea and can reduce
                    absorption of other minerals such as copper.
                  </td>
                </tr>
                <tr>
                  <td>ALA (α-linolenic acid)</td>
                  <td>
                    Low intake of omega-3 fats, including ALA, may mean fewer
                    heart-protective benefits and can affect overall fatty acid
                    balance.
                  </td>
                  <td>
                    High intakes from oils and seeds mainly add extra calories;
                    very high supplement doses of omega-3s can increase bleeding
                    risk in some people.
                  </td>
                </tr>
                <tr>
                  <td>Monounsaturated Fat (MUFA)</td>
                  <td>
                    No specific deficiency disease is defined, but very low
                    unsaturated fat alongside high refined starch may worsen
                    blood lipid patterns.
                  </td>
                  <td>
                    Diets high in MUFA-rich oils can be part of a heart-healthy
                    pattern, but very high total fat still increases energy
                    intake.
                  </td>
                </tr>
                <tr>
                  <td>n-3 PUFA (Total)</td>
                  <td>
                    Low omega-3 intake is linked with fewer heart and brain
                    benefits and may contribute to higher triglycerides.
                  </td>
                  <td>
                    Very high doses from supplements can increase bleeding
                    tendency and should only be used under medical guidance.
                  </td>
                </tr>
                <tr>
                  <td>Polyunsaturated Fat (PUFA)</td>
                  <td>
                    Low PUFA with high saturated fat may raise LDL (“bad”)
                    cholesterol and cardiovascular risk.
                  </td>
                  <td>
                    High PUFA intake adds calories and, depending on the balance
                    of omega-6 to omega-3, may influence inflammation and lipid
                    levels.
                  </td>
                </tr>
                <tr>
                  <td>Saturated Fat (SFA)</td>
                  <td>
                    No deficiency is defined; many dietary guidelines encourage
                    keeping saturated fat relatively low within total fat intake.
                  </td>
                  <td>
                    High saturated fat intake is associated with higher LDL
                    cholesterol and increased cardiovascular risk in many
                    populations.
                  </td>
                </tr>
                <tr>
                  <td>EPA + DHA</td>
                  <td>
                    Low long-chain omega-3 intake (EPA and DHA) may reduce some
                    of the heart and brain benefits seen in higher-fish diets.
                  </td>
                  <td>
                    High supplemental doses can increase bleeding risk and may
                    interact with some medicines, so are usually used under
                    medical supervision.
                  </td>
                </tr>
                <tr>
                  <td>Trans Fat (TFA)</td>
                  <td>
                    Keeping trans fat intake as low as possible is advised;
                    there is no known requirement or benefit.
                  </td>
                  <td>
                    Diets high in industrial trans fats raise LDL and lower HDL
                    cholesterol and are strongly linked with increased heart
                    disease risk.
                  </td>
                </tr>
                <tr>
                  <td>Total Fat</td>
                  <td>
                    Extremely low fat intake can impair absorption of
                    fat-soluble vitamins (A, D, E and K) and essential fatty
                    acids.
                  </td>
                  <td>
                    Very high total fat intake, especially from energy-dense
                    foods, can contribute to excess calorie intake and weight
                    gain.
                  </td>
                </tr>
                <tr>
                  <td>BMI &amp; Body Fat</td>
                  <td>
                    Very low BMI and body fat (underweight) are linked with
                    higher risk of nutrient deficiencies, weakened immunity and
                    hormonal problems.
                  </td>
                  <td>
                    High BMI and body fat (overweight or obesity) are associated
                    with a higher risk of type 2 diabetes, heart disease and
                    other conditions.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="nutrient-disclaimer">
            These are simplified examples based on population research and are
            not a diagnosis or treatment plan. If you have symptoms or are
            worried about your nutrient status or weight, please speak with a
            doctor or registered dietitian.
          </p>
        </div>
      </section>
    </main>
  );
};

export default AiNutritionLandingPage;